import React, { useCallback, useEffect, useState } from 'react'

import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Box, CircularProgress, IconButton, TextField, Typography } from '@mui/material'
import { useSelector } from 'react-redux'

import { validateDiscountCode } from '../../../redux/states/payments/validateDiscountCode/validateDiscountCode.slice'
import {
  ValidateDiscountCodeRequestPayload,
  ValidateDiscountCodeState
} from '../../../redux/states/payments/validateDiscountCode/validateDiscountCode.types'
import { RootState } from '../../../redux/store/store.types'
import { useAppDispatch } from '../../hooks/redux.hooks'
import formStyles from '../Form/Form.styles'
import styles from './DiscountCode.styles'
import { DiscountCodeProps } from './DiscountCode.types'

const DiscountCode: React.FC<DiscountCodeProps> = (props) => {
  const { value, onChange, placeholder = 'Enter discount code', label, showTitle = true, variant = 'outlined' } = props

  const dispatch = useAppDispatch()
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null)

  const validateDiscountCodeState = useSelector(
    (state: RootState) => state.validateDiscountCode
  ) as ValidateDiscountCodeState

  const { validationStatus, loading, lastValidatedCode } = validateDiscountCodeState

  // Debounced validation function
  const debouncedValidation = useCallback(
    (code: string) => {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }

      const timer = setTimeout(() => {
        if (code.trim() && code !== lastValidatedCode) {
          const request: { code: string } = {
            code: code.trim()
          }

          const payload: ValidateDiscountCodeRequestPayload = {
            request
          }

          dispatch(validateDiscountCode(payload))
        }
      }, 500)

      setDebounceTimer(timer)
    },
    [dispatch, debounceTimer, lastValidatedCode]
  )

  // Handle input change
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value

    onChange(event)

    if (newValue.trim()) {
      debouncedValidation(newValue)
    }
  }

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }
    }
  }, [debounceTimer])

  // Get the appropriate end adornment based on validation status
  const getEndAdornment = () => {
    if (!value?.trim()) return null

    if (loading || validationStatus === 'validating') {
      return (
        <IconButton disabled>
          <CircularProgress size={20} />
        </IconButton>
      )
    }

    if (validationStatus === 'valid' && value.trim() === lastValidatedCode) {
      return (
        <IconButton disabled>
          <CheckCircleIcon sx={{ color: 'success.main' }} />
        </IconButton>
      )
    }

    if (validationStatus === 'invalid' && value.trim() === lastValidatedCode) {
      return (
        <IconButton disabled>
          <CancelIcon sx={{ color: 'error.main' }} />
        </IconButton>
      )
    }

    return null
  }

  return (
    <Box sx={styles.container}>
      {showTitle && (
        <Typography variant="h6" sx={styles.title}>
          Discount Code
        </Typography>
      )}

      <TextField
        fullWidth
        variant={variant}
        label={label}
        placeholder={placeholder}
        value={value || ''}
        onChange={handleChange}
        sx={formStyles.textField(true, false)}
        slotProps={{
          input: {
            endAdornment: getEndAdornment()
          }
        }}
      />
    </Box>
  )
}

export default DiscountCode
